import type { QuestionFigure } from '@padcanvas/shared';
import { useMemo, useState, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { DataTable, SearchFilterBar, ScoreCards, formatDateTime, formatMonthDaySlashTime24, IdControl, ImageViewer, StudentWorkImages, type ColumnDef, type FilterDef, type SortState, type ScoreCardItem } from '../components';
import { QUESTION_TYPE_LABELS, type QuestionType } from '@padcanvas/shared';
import { SCORING_LABELS } from '../scoring-labels';
import { LatexText, QuestionFigures } from '../latex';
import { ThreadStatusBadge } from '../threads/ThreadStatusBadge';
import { StudentTeacherStatusBadge } from '../threads/StudentTeacherStatusBadge';
import { ThreadMessagesPanel } from '../threads/ThreadMessagesPanel';
import type { ThreadApi, ThreadApiClient } from '../threads/threadApi';
import { isFeedbackKind, type Thread, type HelpRequestContext, type StudentTeacherKind } from '../threads/types';
import './HelpQueriesView.css';

export interface UserSummary {
    id: string;
    name: string;
    email: string;
    type?: string;
    grade?: number | string | null;
    batchName?: string;
    targetExams?: string[];
}

export interface HelpQueriesViewProps {
    /** Role of the current viewer. Drives sensible defaults for filters, columns, permissions, and labels. */
    role?: 'student' | 'teacher' | 'admin';
    threadApi: ThreadApi;
    /** Immutable filters that scope the list (e.g. ownerId='me', type='student_teacher'). */
    listFilters: {
        ownerId?: string;
        createdBy?: string;
        assignedTo?: string;
        type?: string;
    };
    /** Offer a Doubts / Feedback filter in the bar. Applied to the loaded list. */
    showKindFilter?: boolean;
    /** React-query cache key for the list. */
    queryKey: readonly unknown[];
    /** Additional query keys to invalidate on mutation success (e.g. unread-count). */
    invalidateOnChange?: readonly (readonly unknown[])[];
    /** Current user id — used for unread detection and editing own messages. */
    currentUserId?: string;
    /** Current user display name — used as the creator label when current user IS the creator (e.g. student viewing own threads). */
    currentUserName?: string;

    /** Unified user lookup for resolving thread.createdBy/ownerId to a name/email/etc. Used by teacher and admin views. */
    loadUsers?: () => Promise<UserSummary[]>;
    /** Navigate to a user's detail page — used by admin view for the Teacher column link and student detail name link. */
    onNavigateToUser?: (userId: string) => void;

    /** Whether the current user can post replies. Default true. */
    canReply?: boolean;
    /** Whether the current user can change thread status. Default true. */
    canChangeStatus?: boolean;
    /** Which status actions to expose in the detail panel. Default ['resolve', 'reopen']. */
    statusActions?: Array<'resolve' | 'close' | 'reopen'>;
    /** Whether to mark threads as seen when opened. Default true. */
    markSeenOnView?: boolean;

    /** Page title. Default "Help Queries". Empty = no header (the page supplies one). */
    title?: string;
    /** Empty state message. */
    emptyMessage?: string;
    /** Participant column label. Default "Student". */
    participantLabel?: string;
    /** Derive participant display name (e.g. teacher name instead of student). */
    getParticipant?: (thread: Thread) => string;
    /** Optional extra column (e.g. Owner/Teacher ID link in admin view). */
    extraColumn?: ColumnDef<Thread>;
    /** Several extra columns, inserted after the participant column in order.
     *  Use instead of `extraColumn` when a view needs more than one — e.g. the
     *  admin list wants Teacher AND a date up front, because the built-in
     *  `Created On` sits behind six other columns and overflows off-screen at
     *  laptop widths, where there is no horizontal scroll to reach it. */
    extraColumns?: ColumnDef<Thread>[];
    /** Columns placed BEFORE the participant column, in the order given. For a
     *  view whose natural first column is not the person — the admin doubts
     *  list reads date-first — since `extraColumns` can only land after it. */
    leadingColumns?: ColumnDef<Thread>[];
    /** Hide the built-in `Created On` column — for views that surface the date
     *  themselves via `extraColumns` and would otherwise show it twice. */
    hideCreated?: boolean;
    /** Hide the Replies count column. */
    hideReplies?: boolean;
    /** Hide the Subject column (e.g. when the teacher only teaches one subject). */
    hideSubject?: boolean;
    /** Hide the Participant column (e.g. for student view where the recipient is implicit). */
    hideParticipant?: boolean;
    /** Extra filter inputs to render in the filter bar (e.g. student email, teacher). Drafts are owned by the parent. */
    extraFilterUI?: ReactNode;
    /** Extra applied filters to merge into the API call (e.g. createdBy, ownerId resolved from email lookups). */
    extraListFilters?: { ownerId?: string; createdBy?: string; assignedTo?: string };
    /** Cache key fragment for the extra filters — include these in queryKey so the query re-runs when applied. */
    extraFilterKey?: unknown;
    /** Called when the Fetch button is clicked — parent should commit its draft filters to applied. */
    onFetchClick?: () => void;
    /** Detail page title. Default "Help Query". */
    detailTitle?: string;
    /** Api client used to fetch /questions/{id} for full question rendering in the detail view. */
    apiClient?: ThreadApiClient;
    /** Override the question endpoint path. Default '/questions'. */
    questionPath?: string;
    /** When provided, fetches submission details (scores, time, student work) for submission-linked threads. */
    loadSubmission?: (submissionId: string) => Promise<SubmissionDetails | null>;
    /** When provided, renders a "Student" section in the detail view (typically only for teacher/admin views). */
    renderStudentDetail?: (thread: Thread) => ReactNode;
    /** When provided, renders a "Teacher" section beside the student one — who
     *  the doubt was raised to (`thread.ownerId`). Admin view only; a teacher
     *  looking at their own queue already knows. */
    renderTeacherDetail?: (thread: Thread) => ReactNode;
    /** Display name for the description author (creator) in the conversation. Defaults to the first message sender. */
    getCreatorName?: (thread: Thread) => string;
    /** Navigate to the linked object (e.g. question detail page). Called with objectType and objectId. */
    onNavigateToObject?: (objectType: string, objectId: string, thread?: Thread) => void;
}

export interface FullQuestionData {
    id: string;
    questionText: string;
    questionDiagramUrl?: string | null;
    figures?: QuestionFigure[] | null;
    questionType?: string | null;
    options?: { label: string; text: string; imageUrl?: string | null; isCorrect?: boolean }[] | null;
    correctAnswer?: string;
    subject?: string;
    moduleName?: string | null;
}

/** Submission details rendered in the doubt detail page for submission-linked threads. */
export interface SubmissionDetails {
    id: string;
    submittedAt?: string;
    timeTakenSeconds?: number;
    solutionImageUrl?: string | null;
    /** The work as its pages, when it spans more than one. */
    solutionPageUrls?: string[];
    typedText?: string;
    score?: number;
    scoreComment?: string;
    correctnessScore?: number;
    correctnessScoreComment?: string;
    approachScore?: number;
    approachScoreComment?: string;
    speedScore?: number;
    speedScoreComment?: string;
}

/** Loose shape for any per-app question type — fields used to build FullQuestionData. */
interface QuestionLike {
    id: string;
    questionText: string;
    questionDiagramUrl?: string | null;
    figures?: QuestionFigure[] | null;
    questionType?: string | null;
    options?: { label: string; text: string; imageUrl?: string | null; isCorrect?: boolean }[] | null;
    correctAnswer?: string;
    subject?: string;
    moduleName?: string | null;
    module?: { name?: string } | null;
}

type StatusFilter = '' | 'open' | 'replied' | 'resolved';
type KindGroupFilter = '' | 'doubt' | 'feedback';

/** What a thread is about, as its reader would say it. */
export function studentTeacherKindLabel(kind: StudentTeacherKind | undefined, role: 'student' | 'teacher' | 'admin'): string {
    // Short: it sits in a narrow "About" column, beside the title it is about.
    if (role === 'student') {
        switch (kind) {
            case 'solution_feedback': return 'Solution';
            case 'assignment_feedback': return 'Assignment';
            default: return 'Doubt';
        }
    }
    switch (kind) {
        case 'solution_feedback': return 'Feedback: solution';
        case 'assignment_feedback': return 'Feedback: assignment';
        case 'solution_doubt': return 'Doubt: solution';
        default: return 'Doubt: question';
    }
}

/** The words that opened the thread: the student's description on a doubt, the
 *  teacher's first message on feedback (whose description is empty). */
function openingText(thread: Thread): string {
    return thread.description || thread.messages[0]?.text || '';
}

/** When something was last said: the latest message or edit, else the creation.
 *  Not `_ts` — that moves on any write, mark-seen included, so opening a thread
 *  would read as it being updated. */
function lastActivity(thread: Thread): string {
    let latest = thread.editedAt && thread.editedAt > thread.createdAt ? thread.editedAt : thread.createdAt;
    for (const m of thread.messages) {
        const at = m.editedAt && m.editedAt > m.createdAt ? m.editedAt : m.createdAt;
        if (at > latest) latest = at;
    }
    return latest;
}

/** What the thread is on — a question, or an assignment — for the list row. */
function subjectLine(thread: Thread): string {
    const ctx = thread.context as { questionTitle?: string; assignmentTitle?: string } | undefined;
    return ctx?.questionTitle || ctx?.assignmentTitle || thread.title || '—';
}

/** The status filter, in the same words as the badge: `open` is waiting on the
 *  teacher, `replied` on the student. */
function statusFilterDefs(role: 'student' | 'teacher' | 'admin'): FilterDef[] {
    const [open, replied] = role === 'student'
        ? ['Sent, awaiting teacher', 'From teacher']
        : role === 'teacher'
            ? ['Awaiting your reply', 'Sent, awaiting student']
            : ['Open', 'Replied'];
    return [{
        key: 'status',
        placeholder: 'All Status',
        options: [
            { value: 'open', label: open },
            { value: 'replied', label: replied },
            { value: 'resolved', label: 'Resolved' },
        ],
    }];
}

const KIND_FILTER_DEF: FilterDef = {
    key: 'kind',
    placeholder: 'Doubts & feedback',
    options: [
        { value: 'doubt', label: 'Doubts' },
        { value: 'feedback', label: 'Feedback' },
    ],
};

function defaultGetParticipant(thread: Thread): string {
    return thread.messages[0]?.senderName || '—';
}

export function HelpQueriesView({
    role = 'teacher',
    threadApi,
    showKindFilter = false,
    listFilters,
    queryKey,
    invalidateOnChange = [],
    currentUserId,
    currentUserName,
    canReply = true,
    canChangeStatus = true,
    statusActions = ['resolve', 'reopen'],
    markSeenOnView = true,
    title = 'Help Queries',
    emptyMessage = 'No help queries yet.',
    participantLabel = 'Student',
    getParticipant = defaultGetParticipant,
    extraColumn,
    extraColumns,
    leadingColumns,
    hideCreated = false,
    hideSubject = false,
    hideReplies = false,
    hideParticipant = false,
    detailTitle = 'Help Query',
    apiClient,
    questionPath = '/questions',
    loadSubmission,
    renderStudentDetail,
    renderTeacherDetail,
    getCreatorName,
    extraFilterUI,
    extraListFilters,
    extraFilterKey,
    onFetchClick,
    onNavigateToObject,
}: HelpQueriesViewProps) {
    const queryClient = useQueryClient();
    // Students and teachers read the status as whose move it is; admins see the stored one.
    const statusBadge = (t: Thread) => role === 'admin'
        ? <ThreadStatusBadge status={t.status} />
        : <StudentTeacherStatusBadge thread={t} viewer={role} />;
    const [statusApplied, setStatusApplied] = useState<StatusFilter>('');
    const [kindApplied, setKindApplied] = useState<KindGroupFilter>('');
    const [selectedThread, setSelectedThread] = useState<Thread | null>(null);
    const [sort, setSort] = useState<SortState>({ sortBy: 'updated', sortOrder: 'desc' });

    const linkedSubmissionId = selectedThread?.objectType === 'submission' ? selectedThread.objectId : undefined;
    const { data: fullSubmission } = useQuery({
        queryKey: ['hq-submission', linkedSubmissionId],
        queryFn: () => loadSubmission!(linkedSubmissionId!),
        enabled: !!loadSubmission && !!linkedSubmissionId,
        staleTime: 30_000,
    });

    const linkedQuestionId = selectedThread?.objectType === 'question' ? selectedThread.objectId : undefined;
    const { data: fullQuestion } = useQuery({
        queryKey: ['hq-question', questionPath, linkedQuestionId],
        queryFn: async (): Promise<FullQuestionData | null> => {
            const q = await apiClient!.fetchApi<QuestionLike>(`${questionPath}/${linkedQuestionId}`);
            if (!q) return null;
            return {
                id: q.id,
                questionText: q.questionText,
                questionDiagramUrl: q.questionDiagramUrl,
                figures: q.figures,
                questionType: q.questionType,
                options: q.options?.map(o => ({
                    label: o.label,
                    text: o.text,
                    imageUrl: o.imageUrl,
                    isCorrect: o.isCorrect,
                })) ?? null,
                correctAnswer: q.correctAnswer,
                subject: q.subject,
                moduleName: q.moduleName ?? q.module?.name ?? null,
            };
        },
        enabled: !!apiClient && !!linkedQuestionId,
        staleTime: 5 * 60 * 1000,
    });

    const fullQueryKey = useMemo(
        () => [...queryKey, statusApplied, extraFilterKey],
        [queryKey, statusApplied, extraFilterKey]
    );

    const { data, isLoading, isFetching } = useQuery({
        queryKey: fullQueryKey,
        queryFn: () => threadApi.list({
            ...listFilters,
            ...extraListFilters,
            status: statusApplied || undefined,
            limit: 100,
        }),
        refetchInterval: 60_000,
        refetchOnMount: 'always',
    });

    const handleFilterChange = (vals: Record<string, string>) => {
        setStatusApplied((vals.status || '') as StatusFilter);
        setKindApplied((vals.kind || '') as KindGroupFilter);
    };

    const invalidateAll = () => {
        queryClient.invalidateQueries({ queryKey });
        invalidateOnChange.forEach(k => queryClient.invalidateQueries({ queryKey: k }));
    };

    const threads = useMemo(() => {
        const items = (data?.items || []).filter(t =>
            !kindApplied || (kindApplied === 'feedback') === isFeedbackKind(t.kind));
        if (!sort) return items;
        const STATUS_ORDER: Record<string, number> = { open: 0, in_progress: 1, replied: 2, resolved: 3, closed: 4 };
        const getKey = (t: Thread): number | string => {
            if (sort.sortBy === 'created') return new Date(t.createdAt).getTime();
            if (sort.sortBy === 'updated') return new Date(lastActivity(t)).getTime();
            if (sort.sortBy === 'status') return STATUS_ORDER[t.status] ?? 99;
            return 0;
        };
        return [...items].sort((a, b) => {
            const av = getKey(a);
            const bv = getKey(b);
            const cmp = av < bv ? -1 : av > bv ? 1 : 0;
            return sort.sortOrder === 'asc' ? cmp : -cmp;
        });
    }, [data, sort, kindApplied]);

    const handleSelectThread = (thread: Thread) => {
        setSelectedThread(thread);
        if (markSeenOnView) {
            // Fire-and-forget mark-seen
            threadApi.update(thread.id, thread.ownerId, { markSeen: true })
                .then(() => invalidateAll())
                .catch(() => { /* ignore */ });
        }
    };

    // ── Detail view ────────────────────────────────────────────────
    if (selectedThread) {
        const ctx = selectedThread.context as HelpRequestContext | undefined;
        const questionType = fullQuestion?.questionType;
        const correctAnswerNorm = fullQuestion?.correctAnswer?.trim().toLowerCase();
        const isSubmissionThread = selectedThread.objectType === 'submission';
        return (
            <div className="hq-detail">
                <div className="hq-detail-header">
                    <div className="hq-detail-header-main">
                        <button className="hq-back-btn" onClick={() => setSelectedThread(null)}>&larr; Back</button>
                        <h1 className="hq-detail-title">{detailTitle}</h1>
                        {statusBadge(selectedThread)}
                    </div>
                </div>

                <div className="hq-detail-body">
                    {renderStudentDetail && (
                        <div className="hq-student-section">
                            <div className="hq-section-label">Student</div>
                            {renderStudentDetail(selectedThread)}
                        </div>
                    )}

                    {renderTeacherDetail && (
                        <div className="hq-student-section">
                            <div className="hq-section-label">Teacher</div>
                            {renderTeacherDetail(selectedThread)}
                        </div>
                    )}

                    {selectedThread.objectType === 'question' && selectedThread.objectId && (
                        <div className="hq-linked-entity">
                            <div className="hq-linked-entity-header">
                                <span className="hq-linked-entity-type">Question</span>
                                <div className="hq-linked-entity-meta">
                                    {questionType && <span className="hq-qtype">{QUESTION_TYPE_LABELS[questionType as QuestionType] ?? questionType}</span>}
                                    {fullQuestion?.moduleName && <span className="hq-module">{fullQuestion.moduleName}</span>}
                                </div>
                                {onNavigateToObject && (
                                    <button
                                        className="hq-linked-entity-link"
                                        onClick={() => onNavigateToObject(selectedThread.objectType!, selectedThread.objectId!, selectedThread)}
                                    >
                                        View Question &rarr;
                                    </button>
                                )}
                            </div>
                            {fullQuestion ? (
                                <div className="hq-question-full">
                                    <div className="hq-question-text"><LatexText text={fullQuestion.questionText} figures={fullQuestion.figures} /></div>
                                    {fullQuestion.questionDiagramUrl && (
                                        <ImageViewer
                                            src={fullQuestion.questionDiagramUrl}
                                            alt="Question diagram"
                                            className="hq-question-image"
                                        />
                                    )}
                                    <QuestionFigures text={fullQuestion.questionText} figures={fullQuestion.figures} className="hq-question-image" />
                                    {fullQuestion.options && fullQuestion.options.length > 0 && (
                                        <div className="hq-question-options">
                                            {fullQuestion.options.map((opt, idx) => {
                                                const isCorrect = opt.isCorrect === true
                                                    || (correctAnswerNorm !== undefined && opt.label.trim().toLowerCase() === correctAnswerNorm);
                                                return (
                                                    <div
                                                        key={idx}
                                                        className={`hq-question-option ${isCorrect ? 'hq-question-option--correct' : ''}`}
                                                    >
                                                        <span className="hq-option-label">{opt.label.toUpperCase()}.</span>
                                                        <span className="hq-option-text"><LatexText text={opt.text} /></span>
                                                        {opt.imageUrl && (
                                                            <ImageViewer src={opt.imageUrl} alt={opt.label} className="hq-option-image" />
                                                        )}
                                                        {isCorrect && <span className="hq-option-correct-badge">Correct</span>}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                    {!fullQuestion.options?.length && fullQuestion.correctAnswer && (
                                        <div className="hq-correct-answer">
                                            <span className="hq-correct-answer-label">Correct Answer:</span>
                                            <span className="hq-correct-answer-value"><LatexText text={fullQuestion.correctAnswer} /></span>
                                        </div>
                                    )}
                                </div>
                            ) : ctx?.questionTitle ? (
                                <div className="hq-linked-entity-content">
                                    <LatexText text={ctx.questionTitle} />
                                </div>
                            ) : null}
                        </div>
                    )}

                    {isSubmissionThread && selectedThread.objectId && (
                        <div className="hq-linked-entity">
                            <div className="hq-linked-entity-header">
                                <span className="hq-linked-entity-type">Submission Details</span>
                                <IdControl value={selectedThread.objectId} container="submissions" display="View Full Submission" />
                                {onNavigateToObject && (
                                    <button
                                        className="hq-linked-entity-link"
                                        onClick={() => onNavigateToObject('submission', selectedThread.objectId!, selectedThread)}
                                    >
                                        View Attempt &rarr;
                                    </button>
                                )}
                            </div>
                            {(fullQuestion?.questionText || ctx?.questionTitle) && (
                                <div className="hq-subsection">
                                    <div className="hq-subsection-label">Question</div>
                                    <div className="hq-question-text">
                                        <LatexText text={fullQuestion?.questionText || ctx?.questionTitle || ''} />
                                    </div>
                                </div>
                            )}
                            {fullSubmission?.submittedAt && (
                                <div className="hq-context-row hq-context-row--spaced">
                                    <span className="hq-context-label">Submitted:</span>
                                    <span className="hq-context-value">{formatDateTime(fullSubmission.submittedAt)}</span>
                                </div>
                            )}
                            {fullSubmission && (fullSubmission.score !== undefined || fullSubmission.correctnessScore !== undefined) && (
                                <ScoreCards
                                    items={[
                                        { label: 'Overall Score', value: fullSubmission.score, suffix: '/10', comment: fullSubmission.scoreComment },
                                        { label: SCORING_LABELS.correctness, value: fullSubmission.correctnessScore, comment: fullSubmission.correctnessScoreComment },
                                        { label: SCORING_LABELS.approach, value: fullSubmission.approachScore, comment: fullSubmission.approachScoreComment },
                                        { label: SCORING_LABELS.speed, value: fullSubmission.speedScore, comment: fullSubmission.speedScoreComment },
                                    ] satisfies ScoreCardItem[]}
                                />
                            )}
                            {(fullSubmission?.solutionImageUrl || fullSubmission?.typedText || ctx?.solutionImageUrl || ctx?.typedText) && (
                                <div className="hq-context-row hq-context-row--column">
                                    <span className="hq-context-label">Student's solution:</span>
                                    {(fullSubmission?.solutionImageUrl || ctx?.solutionImageUrl) && (
                                        <StudentWorkImages
                                            imageUrl={fullSubmission?.solutionImageUrl || ctx?.solutionImageUrl || ''}
                                            pageUrls={fullSubmission?.solutionPageUrls}
                                            alt="Student solution"
                                            className="hq-solution-thumbnail"
                                        />
                                    )}
                                    {(fullSubmission?.typedText || ctx?.typedText) && (
                                        <div className="hq-context-value"><LatexText text={fullSubmission?.typedText || ctx?.typedText || ''} /></div>
                                    )}
                                </div>
                            )}
                        </div>
                    )}

                    {selectedThread.objectType === 'assignment_attempt' && selectedThread.objectId && (
                        <div className="hq-linked-entity">
                            <div className="hq-linked-entity-header">
                                <span className="hq-linked-entity-type">Assignment attempt</span>
                                {onNavigateToObject && (
                                    <button
                                        className="hq-linked-entity-link"
                                        onClick={() => onNavigateToObject('assignment_attempt', selectedThread.objectId!, selectedThread)}
                                    >
                                        View Attempt &rarr;
                                    </button>
                                )}
                            </div>
                            <div className="hq-linked-entity-content">{subjectLine(selectedThread)}</div>
                        </div>
                    )}

                    {!isSubmissionThread && ctx && (ctx.solutionImageUrl || ctx.typedText) && (
                        <div className="hq-context-panel">
                            {ctx.solutionImageUrl && (
                                <div className="hq-context-row">
                                    <span className="hq-context-label">Student's work:</span>
                                    <ImageViewer src={ctx.solutionImageUrl} alt="Student solution" className="hq-solution-thumbnail" />
                                </div>
                            )}
                            {ctx.typedText && (
                                <div className="hq-context-row">
                                    <span className="hq-context-label">Answer:</span>
                                    <span className="hq-context-value"><LatexText text={ctx.typedText} /></span>
                                </div>
                            )}
                        </div>
                    )}

                    <ThreadMessagesPanel
                        thread={selectedThread}
                        threadApi={threadApi}
                        currentUserId={currentUserId}
                        currentUserName={currentUserName}
                        creatorName={getCreatorName?.(selectedThread)}
                        canReply={canReply}
                        canChangeStatus={canChangeStatus}
                        statusActions={statusActions}
                        onThreadChanged={(t) => { setSelectedThread(t); invalidateAll(); }}
                        invalidateOnChange={[queryKey, ...invalidateOnChange]}
                    />
                </div>
            </div>
        );
    }

    // ── List view ──────────────────────────────────────────────────
    const columns: ColumnDef<Thread>[] = [
        ...(!hideParticipant ? [{
            key: 'participant',
            header: participantLabel,
            render: (row: Thread) => <span className="hq-participant">{getParticipant(row)}</span>,
            width: '160px',
        }] : []),
        {
            key: 'about',
            header: 'About',
            render: (row: Thread) => <span className="hq-kind">{studentTeacherKindLabel(row.kind, role)}</span>,
            width: '150px',
        },
        {
            key: 'question',
            header: 'On',
            mobile: 'title',
            render: (row) => <span className="hq-question"><LatexText text={subjectLine(row)} truncate={120} /></span>,
            // Titles run long ("Teoler Physics Rotational Dynamics – …"); at the
            // table's natural width this column was squeezed to a word a line.
            width: '260px',
        },
        {
            key: 'doubt',
            header: 'Message',
            mobile: 'meta',
            render: (row) => {
                const text = openingText(row) || '—';
                const truncated = text.length > 100 ? text.substring(0, 100) + '…' : text;
                return <span className="hq-doubt">{truncated}</span>;
            },
        },
        ...(!hideSubject ? [{
            key: 'subject',
            header: 'Subject',
            render: (row: Thread) => {
                const ctx = row.context as HelpRequestContext | undefined;
                return ctx?.subject ? <span className="hq-subject">{ctx.subject}</span> : '—';
            },
            width: '120px',
        }] : []),
        {
            key: 'status',
            header: 'Status',
            mobile: 'accessory',
            render: (row) => statusBadge(row),
            width: '110px',
            sortable: true,
        },
        ...(!hideReplies ? [{
            key: 'replies',
            header: 'Replies',
            // A teacher-opened thread's first message is its opening, not a reply.
            render: (row: Thread) => row.messages.length - (row.description ? 0 : 1),
            width: '60px',
        }] : []),
        ...(!hideCreated ? [{
            key: 'created',
            header: 'Created On',
            mobile: 'hide' as const,
            render: (row: Thread) => <span className="hq-time">{formatMonthDaySlashTime24(row.createdAt)}</span>,
            width: '110px',
            sortable: true,
        }] : []),
        {
            key: 'updated',
            header: 'Updated',
            render: (row) => <span className="hq-time">{formatMonthDaySlashTime24(lastActivity(row))}</span>,
            width: '110px',
            sortable: true,
        },
    ];

    // Inserted right after the participant column, in the order given, so the
    // caller's columns land where they are actually read rather than after the
    // question/doubt text that dominates the row width.
    const inserted = extraColumns ?? (extraColumn ? [extraColumn] : []);
    if (inserted.length > 0) {
        columns.splice(hideParticipant ? 0 : 1, 0, ...inserted);
    }
    if (leadingColumns?.length) {
        columns.unshift(...leadingColumns);
    }

    const isUnread = (thread: Thread): boolean => {
        if (!currentUserId) return false;
        const seen = thread.seenBy[currentUserId];
        if (!seen) return true;
        const seenTs = new Date(seen).getTime() / 1000;
        return seenTs < (thread as unknown as { _ts: number })._ts;
    };

    return (
        <div className="hq-page">
            {title && (
                <div className="hq-page-header">
                    <h2>{title}</h2>
                </div>
            )}
            <div className="hq-filter-bar">
                <SearchFilterBar
                    filters={showKindFilter ? [KIND_FILTER_DEF, ...statusFilterDefs(role)] : statusFilterDefs(role)}
                    values={{ status: statusApplied, kind: kindApplied }}
                    onChange={handleFilterChange}
                    onFetch={onFetchClick}
                    hideSearch
                    subtle
                    loading={isFetching}
                >
                    {extraFilterUI}
                </SearchFilterBar>
            </div>
            <DataTable<Thread>
                columns={columns}
                data={threads}
                rowKey={(t) => t.id}
                onRowClick={handleSelectThread}
                rowClassName={(t) => (isUnread(t) ? 'hq-row-unread' : '')}
                loading={isLoading}
                emptyMessage={emptyMessage}
                sort={sort}
                onSortChange={setSort}
                bordered
                compact
                mobileCards
            />
        </div>
    );
}
